/* FormsComponent.js */

"use strict";
const _ = require("lodash");
const JsonPatchModel = require("./util/JsonPatchModel");
const Renderer = require("./FormsRenderer");
const glueJay = require("@gluenetworks/gluejay").Gluejay;
const GlueErrorCode = require("./util/GlueErrorCode");
const jsonPatch = require("fast-json-patch");

/**
 * @typedef RefInfo
 * @type {object}
 * @description The raw information about the reference.
 */
/**
 * @typedef ConstructTypePathInfo
 * @type {string}
 * @description The string is in the format <constructName>/<constructTypeName>
 */
/**
 * @typedef AvailableRefs
 * @type {object.<string,RefInfo>}
 * @description The keys are the unique id of the reference and the values are the RefInfo.
 */
/**
 * @typedef RefInstances
 * @type {object.<ConstructTypePathInfo,AvailableRefs>}
 */

//TODO need to make sure we are handling the conversion of REFs properly...

class FormsComponent extends JsonPatchModel {
    constructor(refResolver, param, labFormattedRefs = true) {
        super();
        this._renderer = new Renderer(this);

        this._formsFromReact = !!param;

        this._refResolver = refResolver;

        this._labFormattedRefs = labFormattedRefs;

        this._registerListeners();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}
    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(() => {
            this._renderer = undefined;
        });
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    _refLaunched(launchDeets) {
        let paths = _.get(launchDeets, "configSettings.Ref.paths");
        if (!paths) {
            let path = _.get(launchDeets, "configSettings.Ref.path");
            if (path) {
                paths = [path];
            }
        }
        if (!_.isEmpty(paths)) {
            this.emit("RefLaunched", launchDeets);
        }
    }
    _triggerInvalidStatusChangedEvent(e) {
        this.emit("InvalidStatusChanged", e);
    }
    /**
     * Get all the existing Refs that are being referenced within the current type information.
     * @returns {Promise.<RefInstances>}
     * @private
     */
    _getExistingRefsFromType() {
        return Promise.resolve().then(() => {
            let epn = this.getExpandedPropNotation();
            if (epn) {
                glueJay.expandJtypeRefs(epn);
                let jtypeFields = glueJay.getJTypeFields(epn);
                let jTypeRefFields = glueJay.getJTypeRefFields(jtypeFields);
                let jTypeRefTypes = glueJay.getJTypeRefTypes(jTypeRefFields);

                return Promise.resolve({}).then(() => {
                    if (
                        this._refResolver &&
                        _.isFunction(this._refResolver.getAllPossibleRefInstances)
                    ) {
                        return this._refResolver.getAllPossibleRefInstances(jTypeRefTypes);
                    }
                });
            } else {
                return Promise.reject(
                    new GlueErrorCode({
                        errorCode: "NoTypeInformationSupplied",
                        errorMsg:
                            "You must first supply the Form Component with valid Type Information before we can expand any references.",
                    })
                );
            }
        });
    }
    _generateEpnFromJtype(jtype) {
        let epn = glueJay.getJTypeInfo(jtype);
        glueJay.expandJtypeRefs(epn);

        return epn;
    }
    _generateJtypeFromEpn(/*epn*/) {
        throw new GlueErrorCode({
            errorCode: "NotYetSupported",
            errorMsg: "We are not yet able to generate a jtype from an expanded prop notation",
        });
    }
    _createLabPath(controlRefInfo) {
        function __getLabPath(instanceInfo) {
            let fullRef = "";
            if (instanceInfo.labPath) {
                let labPath = instanceInfo.labPath;
                let asteriskIdx = labPath.indexOf("*");
                if (asteriskIdx > 0) {
                    labPath = labPath.substring(0, asteriskIdx - 1);
                }
                fullRef = labPath + "/" + instanceInfo.instanceName + ".json";
            }
            return fullRef;
        }

        if (Object.hasOwn(controlRefInfo, "instance")) {
            return __getLabPath(controlRefInfo.instance);
        } else if (Object.hasOwn(controlRefInfo, "instances")) {
            let manyRefs = [];
            controlRefInfo.instances.forEach((refInfo) => {
                manyRefs.push(__getLabPath(refInfo));
            });
            return manyRefs;
        }
    }
    _mutateIntoLabFormat(obj) {
        if (_.isArray(obj)) {
            obj.forEach((child) => {
                this._mutateIntoLabFormat(child);
            });
        } else if (_.isPlainObject(obj)) {
            if (Object.hasOwn(obj, "@ref")) {
                // If the value is not null convert it to lab
                // otherwise null is a valid lab setting for an @ref.
                if (obj["@ref"] !== null) {
                    let refs = this._createLabPath(obj["@ref"]);
                    Object.keys(obj).forEach((key) => {
                        delete obj[key];
                    });
                    obj["@ref"] = refs;
                }
            } else {
                Object.keys(obj).forEach((key) => {
                    this._mutateIntoLabFormat(obj[key]);
                });
            }
        }
    }
    _createControlRefFromLabPath(labRefInfo) {
        function __getControlRefInfoFromLabPath(path) {
            let info = glueJay.parsePath(path);
            info.instanceId = info.instanceName;
            //TODO this is a total hack
            if (_.get(window, "steve.userSession")) {
                let activeOrg = window.steve.userSession.getActiveOrganization();
                info.organizationId = activeOrg.id;
            } else {
                info.organizationId = "UNKNOWN";
            }
            return info;
        }
        let refInfo = {};
        if (_.isString(labRefInfo)) {
            refInfo.instance = __getControlRefInfoFromLabPath(labRefInfo);
        } else if (_.isArray(labRefInfo)) {
            let manyRefs = [];
            labRefInfo.forEach((path) => {
                manyRefs.push(__getControlRefInfoFromLabPath(path));
            });
            refInfo.instances = manyRefs;
        }
        return refInfo;
    }
    _mutateIntoControlFormat(obj) {
        if (_.isArray(obj)) {
            obj.forEach((child) => {
                this._mutateIntoControlFormat(child);
            });
        } else if (_.isPlainObject(obj)) {
            if (Object.hasOwn(obj, "@ref")) {
                let refInfo = this._createControlRefFromLabPath(obj["@ref"]);
                obj["@ref"] = refInfo;
            } else {
                Object.keys(obj).forEach((key) => {
                    this._mutateIntoControlFormat(obj[key]);
                });
            }
        }
    }
    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/
    get typeInformation() {
        return this.getTypeInformation();
    }
    getTypeInformation() {
        return {
            expandedPropNotation: this.expandedPropNotation,
            jtype: this.jtype,
        };
    }
    set typeInformation(ti) {
        this.setTypeInformation(ti);
    }
    setTypeInformation(ti, silent) {
        //TODO _normalize REFs
        let old = this.getTypeInformation();

        //already expanded
        if (Object.hasOwn(ti, "@propertyOrder")) {
            this._expandedPropNotation = ti;
            this._jtype = this._generateJtypeFromEpn(ti);
        } else {
            this._expandedPropNotation = this._generateEpnFromJtype(ti);
            this._jtype = ti;
        }

        if (!silent) {
            this.emit("TypeInformationChanged", this.getTypeInformation(), old);
        }
        return this;
    }
    setPristineValue(val, silent) {
        this.clearPendingPatches(true);
        this._setPristineValue(val, silent);
    }
    get expandedPropNotation() {
        return this.getExpandedPropNotation();
    }
    getExpandedPropNotation() {
        return _.cloneDeep(this._expandedPropNotation);
    }
    get jtype() {
        return this.getJtype();
    }
    getJtype() {
        return _.cloneDeep(this._jtype);
    }
    save() {
        return this.applyPendingPatches();
    }
    getValueInControlFormat() {
        let val = this.getValue();

        return val;
    }
    isFormInvalid() {
        return this._renderer.isFormInvalid();
    }
    getPatchesFromControlFormat(newValue) {
        let labValue = _.cloneDeep(newValue);
        if (this._labFormattedRefs) {
            //this will mutate the value into lab Format
            this._mutateIntoLabFormat(labValue);
        }

        //get the delta from the pristine value (only the value changes caused by the user/form).
        let patches = jsonPatch.compare(this.getPristineValue(), labValue);

        return patches;
    }
    show() {
        this._renderer.rootElement.classList.remove("hidden");
    }
    hide() {
        this._renderer.rootElement.classList.add("hidden");
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /* START PUBLIC RESOLVER METHODS */
    /*******************************************/
    getAllPossibleRefOptions() {
        return this._getExistingRefsFromType();
    }
    /*******************************************/
    /* END PUBLIC RESOLVER METHODS */
    /*******************************************/
}
module.exports = FormsComponent;
